// import { modifier_set } from "@state/_DEFAULTS";
import {
	RATE_CARD_TRUCK_SIZE,
	TFuelLevyType,
	TRateCardModifierType,
	TRateCardSettings,
	TRateCardSettingsPayload,
	TTruckTypeModifier,
	TTruckTypeModifierDetails,
} from '@types';
import { z, ZodFormattedError } from 'zod';
import { getSelectedFuelLevy } from './getSelectedFuelLevy';

const stringToNumberSchema = z
	.string()
	.refine((value) => value !== '', { message: 'Enter a value' })
	.refine((value) => {
		const num = Number(value);
		return !isNaN(num) && num >= 0;
	}, { message: 'Value must be at least 0' }
	);
const stringOrEmptyToNumberSchema = z
	.string()
	.refine((value) => {
		const num = Number(value);
		return !isNaN(num) && num >= 0;
	}, { message: 'Value must be at least 0' }
	);
const flatModifierSchema = z.object({
	type: z.enum([TRateCardModifierType.FIXED]),
	value: stringToNumberSchema
});


export const laneSettingsSchema = z.object({
	rate_card_types: z.array(z.object({
		type: z.enum(['WEIGHT', 'PALLETS', 'FULL_LOAD', 'CPK', 'ITEM']),
		modifier_set: z.object({
			pallet_jack: flatModifierSchema,
			pallet_exchange: flatModifierSchema,
			pallet_transfer: flatModifierSchema,
			tailgate_lift: z.object({
				type: z.enum([TRateCardModifierType.PER_LIFT, TRateCardModifierType.PER_STOP]),
				value: stringToNumberSchema,
				pallets_per_lift: stringToNumberSchema.optional(),
				max_charge: stringOrEmptyToNumberSchema.optional().nullable(),
			}).nullable(),
			day_surcharges: z.object({
				saturday: z.object({
					type: z.enum([TRateCardModifierType.PERCENT, TRateCardModifierType.PER_DAY]),
					value: stringToNumberSchema,
				}).nullable(),
				sunday: z.object({
					type: z.enum([TRateCardModifierType.PERCENT, TRateCardModifierType.PER_DAY]),
					value: stringToNumberSchema,
				}).nullable(),
				public_holiday: z.object({
					type: z.enum([TRateCardModifierType.PERCENT, TRateCardModifierType.PER_DAY]),
					value: stringToNumberSchema,
				}).nullable(),
			}),
			truck_type: z.object({
				details: z.object({
					TIPPER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER_DROP_DECK: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER_MEZZANINE_DECK: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER_STRAIGHT_DECK: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					PANTECH: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					FLAT_TOP: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					SIDELOADER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					SKELETON_TRAILER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					OPEN_TOP: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					UTE: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
				}),
			}),
			tolls: flatModifierSchema,
			admin_fee: flatModifierSchema,
			// Hot fix - 3/4 Will be implemented in phase 2
			/*  hand_unload: flatModifierSchema,
			    forklift: flatModifierSchema,
			    express: flatModifierSchema,
			    demurrage: z.object({
			   	  type: z.enum([TRateCardModifierType.PER_HOUR]),
			   	  value: stringToNumberSchema,
			    }).nullable(), */
			demurrage: z.null(),
			forklift: z.object({
				type: z.enum([TRateCardModifierType.FIXED]),
				value: z.literal('0')
			}),
			express: z.object({
				type: z.enum([TRateCardModifierType.FIXED]),
				value: z.literal('0')
			}),
			hand_unload: z.object({
				type: z.enum([TRateCardModifierType.PER_STOP, TRateCardModifierType.PER_PALLET]),
				value: stringToNumberSchema,
				attracts_fuel_levy: z.boolean().optional(),
			}).nullable(),
			split_trailer: z.object({
				type: z.enum([TRateCardModifierType.PER_STOP]),
				truck_sizes: z.object({
					A_DOUBLE: z.object({ value: stringToNumberSchema }),
					B_DOUBLE: z.object({ value: stringToNumberSchema }),
					B_TRIPLE: z.object({ value: stringToNumberSchema }),
				}),
				attracts_fuel_levy: z.boolean().optional(),
			}).nullable(),
			additional_pickup_stops: z.object({
				type: z.enum([TRateCardModifierType.FIXED, TRateCardModifierType.PER_STOP]),
				value: stringToNumberSchema,
				threshold: z.number().int().min(0),
			}).nullable(),
			additional_drop_off_stops: z.object({
				type: z.enum([TRateCardModifierType.FIXED, TRateCardModifierType.PER_STOP]),
				value: stringToNumberSchema,
				threshold: z.number().int().min(0),
			}).nullable(),
			dangerous_goods: z.object({
				type: z.enum([TRateCardModifierType.FIXED, TRateCardModifierType.PERCENT]),
				value: stringToNumberSchema,
			}).or(z.object({
				type: z.enum([TRateCardModifierType.THRESHOLD_KG]),
				value_below: stringOrEmptyToNumberSchema,
				value_above: stringOrEmptyToNumberSchema,
				threshold_kg: stringToNumberSchema,
			})).nullable()
		}),
		ranges: z.array(
			z.object({
				start: z.number().min(0),
				end: z.number().min(1),
			})
		).nullable(),
		rules: z.object({
			allow_address_change: z.boolean(),
			allow_pickup_stops: z.boolean(),
			allow_weekend: z.boolean(),
			book_start_at_days: z.number().min(0),
			cpk_config_base_price: z.number().optional().nullable(),
			cpk_config_cubic_conversion: z.number().optional().nullable(),
			cpk_config_pallet_height: z.number().min(0).optional().nullable(),
			cpk_config_pallet_length: z.number().min(0).optional().nullable(),
			cpk_config_pallet_width: z.number().min(0).optional().nullable(),
			max_future_book_at_days: z.number().min(0),
			min_drop_at_days: z.number().min(0),
			booking_cutoff: z.string().optional().nullable(),
			allow_consolidation: z.boolean().optional(),
			disable_take_now_price: z.boolean(),
			min_charge: stringOrEmptyToNumberSchema,
		}),
	})),
	full_load: z.array(
		z.enum([
			'RIGID',
			'SINGLE',
			'B_DOUBLE',
			'B_DOUBLE_HIGH_CUBE',
			'SUPER_B_DOUBLE',
			'B_TRIPLE',
			'A_DOUBLE',
			'CONTAINER_20_FT_COASTAL',
			'CONTAINER_40_FT_COASTAL',
			'CONTAINER_46_FT_COASTAL',
			'CONTAINER_48_FT_COASTAL'
		])
	),
	disable_admin_rate_card_price: z.boolean(),
	fuel_levy_type: z.enum(['global_shipper', 'global_carrier', 'customer', 'rate_card']),
	fuel_levy_id: z.string().optional(),
	fuel_levy_next_value: z.number().min(-100).max(100).nullable(),
	fuel_levy_next_start_at: z.string().min(1, { message: 'Next fuel levy start date is required' }).nullable(),
}).superRefine((data, ctx) => {
	const rateCardTypes = data.rate_card_types;
	if (!rateCardTypes.length) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'Please select at least one rate card type',
			path: ['rate_card_types']
		})
	}
	const fullLoadIndex = rateCardTypes.findIndex((rc) => rc.type === 'FULL_LOAD');
	if (fullLoadIndex >= 0 && data.full_load?.length === 0) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'Please select at least one truck size',
			path: ['full_load']
		})
	}
	rateCardTypes.forEach((rc, index) => {
		if (rc.modifier_set.tailgate_lift?.type === TRateCardModifierType.PER_LIFT) {
			if (!rc.modifier_set.tailgate_lift.pallets_per_lift) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Enter a value',
					path: ['rate_card_types', `${index}`, 'modifier_set', 'tailgate_lift', 'pallets_per_lift']
				})
			}
		}
		if (rc.modifier_set.tailgate_lift?.type) {
			if (!rc.modifier_set.tailgate_lift.value) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Enter a value',
					path: ['rate_card_types', `${index}`, 'modifier_set', 'tailgate_lift', 'value']
				})
			}
		}
		if (rc.modifier_set.dangerous_goods?.type === TRateCardModifierType.THRESHOLD_KG) {
			if (!rc.modifier_set.dangerous_goods.value_above && !rc.modifier_set.dangerous_goods.value_below) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'You need to enter a value for fee below or above',
					path: ['rate_card_types', `${index}`, 'modifier_set', 'dangerous_goods', 'fees']
				})
			}
		}
		if (rc.type !== 'FULL_LOAD') {
			if (!rc.ranges || !rc.ranges.length) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'At least one range is required',
					path: ['rate_card_types', `${index}`, 'ranges']
				})
			}
			rc.ranges?.forEach((range, rangeIndex) => {
				if (!range.start && range.start !== 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Range start is missing',
						path: ['rate_card_types', `${index}`, 'ranges']
					})
				}
				if (!range.end) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Range end is missing',
						path: ['rate_card_types', `${index}`, 'ranges']
					})
				}
				if (range.end < range.start) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Starting value must be lower or equal than the end value for each range',
						path: ['rate_card_types', `${index}`, 'ranges']
					})
				}
				const previousRange = rc.ranges?.[rangeIndex - 1];
				if (previousRange && previousRange.end >= range.start) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'This range is overlapping with previous range',
						path: ['rate_card_types', `${index}`, 'ranges']
					})
				} else if (previousRange && previousRange.end + 1 !== range.start) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'There is a gap between this and previous range',
						path: ['rate_card_types', `${index}`, 'ranges']
					})
				}
			})
		}
		const { rules } = rc;
		if (rules.cpk_config_cubic_conversion && rules.cpk_config_cubic_conversion > 1) {
			if (!rules.cpk_config_pallet_height) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Pallet height is missing',
					path: ['rate_card_types', `${index}`, 'rules', 'cpk_config_pallet_height']
				})
			}
			if (!rules.cpk_config_pallet_length) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Pallet length is missing',
					path: ['rate_card_types', `${index}`, 'rules', 'cpk_config_pallet_length']
				})
			}
			if (!rules.cpk_config_pallet_width) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Pallet width is missing',
					path: ['rate_card_types', `${index}`, 'rules', 'cpk_config_pallet_width']
				})
			}
		}
	})
});

export type TLaneSettingsSchema = z.infer<typeof laneSettingsSchema>;

export const buildLaneSettingsAndValidate = (
	payload: TRateCardSettings,
	isCarrierRateCard: boolean = false
): {
	success: boolean;
	errors: ZodFormattedError<TLaneSettingsSchema> | object,
	payload: TRateCardSettingsPayload
} => {
	const sortOrder = ['FULL_LOAD', 'PALLETS', 'WEIGHT', 'CPK', 'ITEM'];

	const sortedRateCards = [...payload.settings.rate_card_types].sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type));
	const convertEmptyTruckTypesValueToZero = (truckTypeModifier: TTruckTypeModifier) => {
		const newObj: Record<string, { type: string; value: string | null | number }> = {};

		const trucks = truckTypeModifier.details;
		for (const key in trucks) {
			if (Object.prototype.hasOwnProperty.call(trucks, key)) {
				newObj[key] = {
					...trucks[key as keyof TTruckTypeModifierDetails],
					value: trucks[key as keyof TTruckTypeModifierDetails].value === '' || trucks[key as keyof TTruckTypeModifierDetails].value === null ? '0' : trucks[key as keyof TTruckTypeModifierDetails].value,
				};
			}
		}
		return {
			details: newObj,
			attracts_fuel_levy: !!truckTypeModifier.attracts_fuel_levy
		} as TTruckTypeModifier;
	}

	const getFuelLevyValues = () => {
		const fuelLevyType = payload.settings.fuel_levy_type as TFuelLevyType;
		const fuelLevyId = payload.settings.fuel_levy_id;
		const fuelLevy = getSelectedFuelLevy(payload.fuel_levies, fuelLevyType, fuelLevyId);
		let fuelLevyNextValue = null;
		let fuelLevyNextStartDate = null;
		if (fuelLevyType !== 'global_shipper' && fuelLevyType !== 'global_carrier') {
			if (
				(
					typeof fuelLevy?.next?.tariff === 'number' ||
					fuelLevy?.next?.tariff
				) &&
				fuelLevy?.next?.start_at
			) {
				fuelLevyNextValue = Number(fuelLevy?.next?.tariff);
				fuelLevyNextStartDate = fuelLevy?.next?.start_at;
			} else if (fuelLevy?.current?.id) {
				/* To prevent saving existing fuel levy as new fuel levy
				   (date will be in the past and an error will be returned) */
				fuelLevyNextValue = null;
				fuelLevyNextStartDate = null;
			} else if (
				(
					typeof fuelLevy?.current?.tariff === 'number' ||
					fuelLevy?.current?.tariff
				) &&
				fuelLevy?.current?.start_at
			) {
				fuelLevyNextValue = Number(fuelLevy?.current?.tariff);
				fuelLevyNextStartDate = fuelLevy?.current?.start_at;
			}
		}

		// The API rejects a levy id sent alongside a tariff
		const writesTariff = fuelLevyNextValue !== null || fuelLevyNextStartDate !== null;

		return {
			fuelLevyType: (fuelLevy?.type as TFuelLevyType) ?? fuelLevyType,
			fuelLevyId: writesTariff ? undefined : fuelLevyId,
			fuelLevyNextValue,
			fuelLevyNextStartDate
		}
	}

	const laneSettingsPayload: TRateCardSettingsPayload = {
		disable_admin_rate_card_price: payload.settings.disable_admin_rate_card_price || false,
		use_pallet_quantity_for_pricing: payload.settings.use_pallet_quantity_for_pricing || false,
		full_load: payload.settings.full_load as RATE_CARD_TRUCK_SIZE[],
		fuel_levy_type: getFuelLevyValues().fuelLevyType,
		...(getFuelLevyValues().fuelLevyId ? { fuel_levy_id: getFuelLevyValues().fuelLevyId } : {}),
		fuel_levy_next_value: getFuelLevyValues().fuelLevyNextValue,
		fuel_levy_next_start_at: getFuelLevyValues().fuelLevyNextStartDate,
		rate_card_types: sortedRateCards
			.filter((rc) => !['HOURS'].includes(rc.type)) // Keeping this in case existing hourly ratecards
			.map((rc) =>
				rc.type === 'FULL_LOAD' ? {
					...rc,
					ranges: [],
					rules: {
						...rc.rules,
						disable_take_now_price: rc.rules.disable_take_now_price || false,
						min_charge: `${rc.rules.min_charge || 0}`
					},
					modifier_set: {
						...rc.modifier_set,
						truck_type: convertEmptyTruckTypesValueToZero(rc.modifier_set.truck_type),
						demurrage: null,
						express: {
							type: TRateCardModifierType.FIXED,
							value: '0'
						},
						forklift: {
							type: TRateCardModifierType.FIXED,
							value: '0'
						}
					}
				} : {
					...rc,
					rules: {
						...rc.rules,
						min_charge: `${rc.rules.min_charge || 0}`,
						disable_take_now_price: rc.type === 'ITEM' ? true : rc.rules.disable_take_now_price || false,
						cpk_config_cubic_conversion: !rc.rules.cpk_config_cubic_conversion ? null : rc.rules.cpk_config_cubic_conversion,
					},
					modifier_set: {
						...rc.modifier_set,
						admin_fee: {
							...rc.modifier_set.admin_fee,
							value: rc.modifier_set?.admin_fee?.value || '0' // Why do we need this here?
						},
						tolls: {
							...rc.modifier_set.tolls,
							value: rc.modifier_set?.tolls?.value || '0' // Why do we need this here?
						},
						truck_type: convertEmptyTruckTypesValueToZero(rc.modifier_set.truck_type),
						demurrage: null,
						express: {
							type: TRateCardModifierType.FIXED,
							value: '0'
						},
						forklift: {
							type: TRateCardModifierType.FIXED,
							value: '0'
						}
					},
				}
			)
	}
	const schema = laneSettingsSchema.superRefine((data, ctx) => {
		if (!isCarrierRateCard) {
			data.rate_card_types.forEach((rc, index) => {
				if (rc.rules.max_future_book_at_days > 60) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Limit bookings to within cannot exceed 60 days',
						path: ['rate_card_types', `${index}`, 'rules', 'max_future_book_at_days']
					});
				}
			});
		}
	});
	const result = schema.safeParse(laneSettingsPayload);

	return {
		success: result.success,
		errors: !result.success ? result.error.format() : {},
		payload: laneSettingsPayload
	}

}